import { useCallback, useEffect, useState } from 'react';
import Order from '../model/Order';
import { useOrderQueries } from './useOrderQueries';

const ORDER_PAGE_SIZE = 5;

export type UseOrderPagesResult = {
  orders: Order[];
  fetchNextPage: () => Promise<void>;
  isLoading: boolean;
  hasMoreOrders: boolean;
};

export function usePaginatedOrders(onError: () => void): UseOrderPagesResult {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [allPagesFetched, setAllPagesFetched] = useState<boolean>(false);

  const { getOrders } = useOrderQueries();

  const loadOrdersForNextPage = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getOrders(currentPage, ORDER_PAGE_SIZE);
      if (result.length !== 0) {
        setOrders((prevState) => [...prevState, ...result]);
        setCurrentPage(currentPage + 1);
        if (result.length < ORDER_PAGE_SIZE) {
          setAllPagesFetched(true);
        }
      } else {
        setAllPagesFetched(true);
      }
    } catch (e) {
      console.warn('Error when fetching customer orders', e);
      onError();
    } finally {
      setIsLoading(false);
    }
  }, [onError, currentPage, getOrders]);

  useEffect(() => {
    loadOrdersForNextPage();
  }, []);

  return {
    orders,
    isLoading,
    hasMoreOrders: !allPagesFetched,
    fetchNextPage: loadOrdersForNextPage,
  };
}
